import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { environment } from "environments/environment";
import { lastValueFrom } from "rxjs";

/** Address used by USPS API */
interface PostalAddress {
  streetAddress: string;
  secondaryAddress: string;
  city: string;
  state: string;
  ZIPCode: string;
  ZIPPlus4: string;

  // extra items in the response that we don't use
  // streetAddressAbbreviation?: string;
  // cityAbbreviation?: string;
  // urbanization?: string;
}

/** Success response from the ValidateAddress api */
interface PostalAddressResponse {
  address: PostalAddress;
  /** Name of company at the address, if known */
  firm: string;
  additionalInfo: any;
  /** Indicates problems with the request - not unique address, etc. */
  corrections: { code: string, text: string }[];
  /** Indicates single/multiple/no match.  code 31 = exact match */
  matches: { code: string, text: string }[];
}

/** Error response from the USPS API */
interface PostalErrorResponse {
  apiVersion: string;
  error: {
    /** 400 = address not found or bad data */
    code: string;
    message: string;
    errors: { title: string, detail: string, code: string }[];
  }
}

/** Validates and corrects addresses using the US Postal Service API. */
@Injectable({ providedIn: 'root' })
export class AddressValidationService {
  constructor(private http: HttpClient) { }

  /** Validate the address and optionally fix the address in the entity.
   * @returns a string with an error message if address is invalid, or null if address is good.
   * Note that address can only be fixed if a match is found.
   */
  async validateEntityAddress(entity: any, prefix: string, fix: boolean) {
    // DISABLED 2026-07-27: USPS revoked Addresses API access on 2026-07-12
    // pending re-authorization via https://cop.usps.com. Every call currently
    // returns a 401-shaped error which then toasts as "Address Validation Error".
    // Short-circuiting to null (== "address is fine") until access is restored.
    // Re-enable by removing this return and restoring the original body below.
    return null;

    // eslint-disable-next-line no-unreachable
    const pa = this.makePostalAddress(entity, prefix);
    const res = await this.callValidationApi(pa);
    const msg = this.getMessageFromResponse(res);
    if (fix && !msg) {
      this.setAddressFields(entity, prefix, (res as PostalAddressResponse).address);
    }
    return msg;
  }

  /** Call the ValidateAddress API on the server, which passes it through to the USPS API and returns the raw response. */
  private async callValidationApi(pa: PostalAddress) {
    const url = environment.apiRoot + "api/Postal/ValidateAddress";
    const res = await lastValueFrom(this.http.post<PostalAddressResponse | PostalErrorResponse>(url, pa));
    return res;
  }

  /** Get corrections text or error message from response.  Null indicates success. */
  private getMessageFromResponse(res: PostalAddressResponse | PostalErrorResponse) {
    if ((res as PostalErrorResponse).error) {
      return "ERROR: " + (res as PostalErrorResponse).error.message;
    }
    res = res as PostalAddressResponse;
    const text = (res.corrections.length && res.corrections[0].text);
    return text || null;
  }

  /** Extract address fields (`billingCity` etc.) from the entity and make a PostalAddress. */
  private makePostalAddress(entity: any, prefix: string) {
    const ezip = entity[prefix + 'ZipCode'] as string;
    const zip = ezip.slice(0, 5).replace(/[^0-9]*/, '');
    const p4 = ezip.slice(6).replace(/[^0-9]*/, '');

    const pa: PostalAddress = {
      streetAddress: entity[prefix + 'Address1'],
      secondaryAddress: entity[prefix + 'Address2'],
      city: entity[prefix + 'City'],
      state: entity[prefix + 'State'],
      ZIPCode: zip,
      ZIPPlus4: p4,
    }
    return pa;
  }

  /** Set address fields (`billingCity` etc.) of the entity from the PostalAddress. */
  private setAddressFields(entity: any, prefix: string, pa: PostalAddress) {
    entity[prefix + 'Address1'] = pa.streetAddress;
    entity[prefix + 'Address2'] = pa.secondaryAddress;
    entity[prefix + 'City'] = pa.city;
    entity[prefix + 'State'] = pa.state;
    entity[prefix + 'ZipCode'] = pa.ZIPCode + (pa.ZIPPlus4 ? ('-' + pa.ZIPPlus4) : '');
    return entity;
  }
}
